import React, { useEffect, useState } from "react";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from "@material-ui/core";
import useStyles from "./styles";
import CrmService, { apiError } from "./crmService";
import { useCrmSettings, useDuplicateMobile } from "./crmUi";

const EMPTY = { parentName: "", mobile: "", childAgeYears: "", program: "", source: "" };

/** Five fields for a walk-in or a phone call; the rest can be filled in later. */
export default function QuickAddDialog({ open, onClose, onSaved, onMoreDetails }) {
  const classes = useStyles();
  const { settings } = useCrmSettings();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setErrors({});
      setMessage("");
    }
  }, [open]);

  const duplicate = useDuplicateMobile(form.mobile, CrmService.checkMobile, open);
  const field = (name) => ({
    value: form[name],
    onChange: (e) => setForm((f) => ({ ...f, [name]: e.target.value })),
    error: Boolean(errors[name] || (name === "mobile" && duplicate)),
    helperText: errors[name] || (name === "mobile" ? duplicate : undefined),
    variant: "outlined",
    size: "small",
    fullWidth: true,
  });

  const save = async () => {
    setSaving(true);
    setErrors({});
    setMessage("");
    try {
      const res = await CrmService.create({ ...form, quick: true });
      onSaved && onSaved(res);
    } catch (err) {
      const e = apiError(err);
      setErrors(e.errors);
      setMessage(Object.keys(e.errors).length ? "" : e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs" classes={{ paper: classes.dialogPaper }}>
      <DialogTitle disableTypography>
        <div style={{ fontSize: 20, fontWeight: 600 }}>Quick add</div>
        <div className={classes.muted}>For a walk-in or a call. Add the rest later.</div>
      </DialogTitle>
      <DialogContent dividers style={{ display: "grid", gap: 14 }}>
        <TextField label="Parent name *" autoFocus {...field("parentName")} />
        <TextField label="Mobile *" inputProps={{ inputMode: "tel" }} {...field("mobile")} />
        <TextField label="Child age (years)" type="number" inputProps={{ min: 0, max: 25, step: 0.5 }} {...field("childAgeYears")} />
        <TextField select label="Program" {...field("program")}>
          {(settings?.programs || []).map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </TextField>
        <TextField select label="Source" {...field("source")}>
          {(settings?.sources || []).filter((s) => s !== "Others" && s !== "Referral").map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        {message && <div className={classes.error}>{message}</div>}
      </DialogContent>
      <DialogActions style={{ padding: "12px 24px", justifyContent: "space-between" }}>
        <Button variant="text" style={{ color: "#0b5cc2" }} onClick={onMoreDetails} disabled={saving}>
          Full form
        </Button>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={save}
            disabled={saving || !form.parentName.trim() || !form.mobile.trim() || Boolean(duplicate)}
            startIcon={saving ? <CircularProgress size={16} style={{ color: "#fff" }} /> : null}
          >
            Save
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}
